import type { StpChartOption, StpChartValueSemantic, StpChartValueSemantics } from '../types'
import type { StpChartTokens } from '../tokens'
import type { OptionItem } from './option-collection'
import { restoreOptionCollection, toOptionArray, toOptionRecord } from './option-collection'
import { formatAmount, formatPercent } from '@/utils/misc-tools'

export interface StpBasePolicyContext {
  tokens: StpChartTokens
  valueSemantic: StpChartValueSemantics
  reducedMotion: boolean
}

type StpTooltipOption = OptionItem<NonNullable<StpChartOption['tooltip']>>
type StpLegendOption = OptionItem<NonNullable<StpChartOption['legend']>>
type StpAriaOption = OptionItem<NonNullable<StpChartOption['aria']>>

function normalizeNumber(value: unknown) {
  if (value === null || value === undefined || value === '')
    return undefined

  const numeric = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(numeric) ? numeric : undefined
}

export function formatStpSemanticValue(value: unknown, semantic: StpChartValueSemantic, abbreviated: boolean) {
  const numeric = normalizeNumber(value)
  if (numeric === undefined)
    return '-'

  if (semantic.kind === 'amount')
    return formatAmount({ value: numeric, currency: semantic.currency }, true, abbreviated, abbreviated ? '' : ' ')

  if (semantic.kind === 'ratio')
    return formatPercent(numeric, { fromRatio: semantic.input === 'ratio' })

  return String(numeric)
}

function getUniformSemantic(valueSemantic: StpChartValueSemantics) {
  const semantics = Array.isArray(valueSemantic) ? valueSemantic : [valueSemantic]
  const first = semantics[0]
  if (!first || !semantics.every(semantic => JSON.stringify(semantic) === JSON.stringify(first)))
    return undefined

  return first
}

/** Applies presentation and accessibility defaults shared by every chart type. */
export function applyStpBasePolicy(source: StpChartOption, context: StpBasePolicyContext): StpChartOption {
  const sourceTooltips = toOptionArray<StpTooltipOption>(source.tooltip)
  const legends = toOptionArray<StpLegendOption>(source.legend).map(legend => ({
    ...legend,
    textStyle: {
      ...legend.textStyle,
      fontFamily: context.tokens.fontFamily,
      fontSize: context.tokens.fontSize,
    },
  }))
  const uniformSemantic = getUniformSemantic(context.valueSemantic)
  const textStyle = toOptionRecord(source.textStyle)
  const aria = toOptionArray<StpAriaOption>(source.aria)[0]
  const tooltips = (sourceTooltips.length ? sourceTooltips : [{}]).map(tooltip => ({
    ...tooltip,
    textStyle: {
      ...tooltip.textStyle,
      fontFamily: context.tokens.fontFamily,
      fontSize: context.tokens.fontSize,
    },
    ...(!tooltip.formatter && uniformSemantic && !tooltip.valueFormatter
      ? { valueFormatter: (value: unknown) => formatStpSemanticValue(value, uniformSemantic, false) }
      : {}),
  }))

  return {
    ...source,
    color: source.color ?? context.tokens.palette,
    animation: context.reducedMotion ? false : source.animation,
    textStyle: {
      ...textStyle,
      fontFamily: context.tokens.fontFamily,
      fontSize: context.tokens.fontSize,
      color: context.tokens.textColor,
    },
    aria: {
      ...aria,
      enabled: aria?.enabled ?? true,
    },
    tooltip: restoreOptionCollection(tooltips, source.tooltip),
    ...(legends.length ? { legend: restoreOptionCollection(legends, source.legend) } : {}),
  }
}
